import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { CLAIM_AIRLINE, lookupByAirlineUrn } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  enterTenantMismatch,
  forceLogout,
  getTenantMismatchState,
  subscribeTenantMismatch,
  type ITenantMismatch,
} from './force-logout';

// Pinned-tenant guard per docs/ui/operator-ui-tds.md §3.2 and
// docs/tenant-isolation-model.md §4.4.
//
// This console is built for ONE airline (VITE_TENANT) and operator-bff serves exactly one
// (its own TENANT). An operator whose account belongs to a different airline is not having
// a session problem — their session is fine, they are simply at the wrong console. Nothing
// they can do in this tab will fix it, so this is a TERMINAL state: no redirect, no retry,
// one explanation and a way out.
//
// Two things can detect it, and both land here:
//
//   1. **The server** — operator-bff rejects with `operator-bff:tenant-mismatch-401@v1`,
//      which `lib/http.ts` latches into the force-logout store. This is the authoritative
//      one: it holds even for a token the client could not inspect.
//   2. **The client** — the token's airline claim disagrees with VITE_TENANT. A pre-flight
//      that saves a round-trip; it cannot be the only check, because a UI check is not a
//      security boundary.
//
// They deliberately render the SAME screen. The condition is identical from the operator's
// side, and sending one path to /forbidden and the other to a terminal screen would be two
// different answers to one question. (/forbidden stays for wrong-ROLE, which is unrelated.)

interface TenantGuardProps {
  children: ReactNode;
}

/** The airline this console was built for, e.g. `latam` → `urn:airline:latam`. */
function expectedAirlineUrn(): string | null {
  const tenant = import.meta.env.VITE_TENANT;
  if (!tenant) return null;
  return `urn:airline:${tenant.toLowerCase()}`;
}

/**
 * Friendly airline name for a URN, per `docs/ui/frontend-conventions.md` §18 — labels are
 * primary, URNs secondary. Falls back to the URN's own tail so an airline missing from the
 * registry still reads as a name rather than rendering blank.
 */
function airlineLabel(urn: string): string {
  const entry = lookupByAirlineUrn(urn);
  if (entry) return entry.displayName;
  const tail = urn.startsWith('urn:airline:') ? urn.slice('urn:airline:'.length) : urn;
  return tail ? tail.toUpperCase() : urn;
}

/** Full-height frame shared by every terminal state, so they cannot drift apart. */
function TerminalFrame({ children }: { children: ReactNode }) {
  return (
    // 100dvh (not vh) for mobile browser chrome, per frontend-conventions §18.1. Colours are
    // semantic tokens only — no brand asset, no image: this screen must render correctly for
    // any tenant, including one whose branding failed to load, which is exactly the kind of
    // deployment likely to be misconfigured in the first place.
    <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4 sm:p-6">
      <Card className="w-full max-w-lg">{children}</Card>
    </div>
  );
}

/** LOADING — the socket is still closing. Brief, but real, and never a dead frame. */
function DisconnectingState() {
  const { t } = useTranslation('admin');
  return (
    <TerminalFrame>
      <CardContent className="flex items-center gap-3 py-10">
        <span
          aria-hidden="true"
          className="size-4 shrink-0 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"
        />
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          {t('tenantGuard.signingOut')}
        </p>
      </CardContent>
    </TerminalFrame>
  );
}

/**
 * ERROR / EMPTY — the rejection itself.
 *
 * EMPTY is the same screen with nothing identifying to show: `expectedAirlineUrn` is null
 * because the deployment could not name its own airline (VITE_TENANT unset, or a body a
 * proxy truncated). The operator still needs a way out, so the frame and the sign-out button
 * stay; only the airline-specific copy drops.
 */
function MismatchState({ mismatch }: { mismatch: ITenantMismatch }) {
  const { t } = useTranslation('admin');
  const expected = mismatch.expectedAirlineUrn;
  const token = mismatch.tokenAirlineUrn;

  return (
    <TerminalFrame>
      <CardHeader>
        <CardTitle>{t('tenantGuard.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {expected ? (
            <>
              {t('tenantGuard.serves')}
              <span className="font-medium text-foreground">{airlineLabel(expected)}</span>
              {token ? (
                <>
                  {t('tenantGuard.belongsTo')}
                  <span className="font-medium text-foreground">{airlineLabel(token)}</span>
                </>
              ) : (
                t('tenantGuard.otherAirline')
              )}
              {t('tenantGuard.suffix')}
            </>
          ) : (
            // EMPTY variant — rejected, but this build cannot name the airline it serves.
            t('tenantGuard.empty')
          )}
        </p>

        <p className="text-sm text-muted-foreground">{t('tenantGuard.help')}</p>

        {/* URNs secondary (§18): diagnostic detail, de-emphasised, wrapping on a phone. */}
        {(expected || token) && (
          <dl className="space-y-1 border-t pt-3 text-xs text-muted-foreground">
            {expected && (
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                <dt className="shrink-0">{t('tenantGuard.detail.console')}</dt>
                <dd className="break-all font-mono">{expected}</dd>
              </div>
            )}
            {token && (
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                <dt className="shrink-0">{t('tenantGuard.detail.account')}</dt>
                <dd className="break-all font-mono">{token}</dd>
              </div>
            )}
          </dl>
        )}

        {/* The only action. Full-width on a phone, intrinsic from sm up. */}
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          onClick={() => void forceLogout({ reason: 'wrong-tenant' })}
        >
          {t('tenantGuard.signOut')}
        </Button>
      </CardContent>
    </TerminalFrame>
  );
}

export function TenantGuard({ children }: TenantGuardProps) {
  const { user, isAuthenticated } = useAuth0();

  // Server-detected rejection. `useSyncExternalStore` rather than local state so every
  // mounted consumer sees the same latch — the store is the single source, exactly as the
  // BFF's tenant identity is on the other side of the wire.
  const state = useSyncExternalStore(subscribeTenantMismatch, getTenantMismatchState);

  // Client-side pre-flight.
  const clientMismatch = useMemo<ITenantMismatch | null>(() => {
    if (!isAuthenticated || !user) return null;
    const expected = expectedAirlineUrn();
    const actual = user[CLAIM_AIRLINE];
    if (!expected || typeof actual !== 'string' || actual === expected) return null;
    return {
      reason: 'airline_claim',
      expectedAirlineUrn: expected,
      tokenAirlineUrn: actual,
      message: null,
    };
  }, [isAuthenticated, user]);

  // Latch the client-detected case through the same door as the server's, so the socket is
  // closed and the token getter dropped either way. Idempotent, so this cannot double-fire.
  useEffect(() => {
    if (clientMismatch) void enterTenantMismatch(clientMismatch);
  }, [clientMismatch]);

  const mismatch = state.mismatch ?? clientMismatch;

  // SUCCESS — the ordinary path: this operator belongs here.
  if (!mismatch) return <>{children}</>;

  // LOADING — detected but teardown has not finished. Covers the gap between the client
  // pre-flight firing and its effect latching, so the screen never flickers error→loading.
  if (state.signingOut || !state.mismatch) return <DisconnectingState />;

  // ERROR (or EMPTY, when the airline cannot be named).
  return <MismatchState mismatch={mismatch} />;
}
